export type MediaSize = 'thumb' | 'card' | 'article' | 'hero'

const widths: Record<MediaSize, number> = {
  thumb: 400,
  card: 800,
  article: 1200,
  hero: 1400,
}

function mediaBase() {
  return (import.meta.env.VITE_MEDIA_BASE_URL ?? '').replace(/\/$/, '')
}

function isLocalPost(image: string) {
  return image.startsWith('/posts/') && image.endsWith('.webp') && !image.endsWith('-800.webp')
}

function localNarrow(image: string) {
  return image.replace(/\.webp$/, '-800.webp')
}

/** Resolve a stored image (full URL, /posts/ path, or bucket key) for a layout size. */
export function mediaUrl(image: string | undefined, size: MediaSize = 'card') {
  if (!image) return ''
  if (image.startsWith('data:') || image.startsWith('blob:')) return image

  if (isLocalPost(image) && (size === 'thumb' || size === 'card')) {
    return localNarrow(image)
  }

  if (/^https?:\/\//i.test(image) || image.startsWith('/')) return image

  const path = image.replace(/^\//, '')
  const base = mediaBase()
  if (!base) return `/api/media/${path}`

  const url = `${base}/${path}`
  const joiner = url.includes('?') ? '&' : '?'
  return `${url}${joiner}w=${widths[size]}&fm=webp`
}

export function mediaSrcSet(image: string | undefined, size: MediaSize = 'card') {
  if (!image) return undefined

  if (!isLocalPost(image)) return undefined

  if (size === 'hero' || size === 'article') {
    return `${localNarrow(image)} 800w, ${image} 1400w`
  }

  return `${localNarrow(image)} ${widths[size]}w`
}

export function mediaSizes(size: MediaSize) {
  switch (size) {
    case 'thumb':
      return '96px'
    case 'card':
      return '(min-width: 768px) 25vw, 50vw'
    case 'article':
      return '(min-width: 1024px) 720px, 100vw'
    case 'hero':
      return '(min-width: 1024px) 50vw, 100vw'
  }
}
